import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { ARTICLE_TYPE } from '@atlaskit/help';
import { fg } from '@atlassian/jira-feature-gating';
import {
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
	HELP_PANEL_VERSION,
} from '@atlassian/jira-help-panel-common-constant/src/index.tsx';
import { useHelpPanelActions } from '@atlassian/jira-help-panel-services/src/components/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	useRightSidebarController,
	useActiveRightSidebarState as useRightSidebarState,
} from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { useTopMenus } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import {
	testIdConcat,
	testIdGenerate,
} from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useIsSimplifiedProject } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { NAVIGATION_ITEM_ID } from '../../common/constants.tsx';
import { Help as Trigger } from '../../common/ui/help-button/index.tsx';
import { useHelpButtonState } from '../../controllers/help-button/index.tsx';
import { useNavigationItemAnalytics } from '../../controllers/navigation-item-analytics/main.tsx';
import { BadgeWithoutClient } from './badge/main.tsx';
import messages from './messages.tsx';
import { SpotlightTargetComponent } from './help-common.tsx';

function HelpNew() {
	const isProjectSimplified = useIsSimplifiedProject();
	const { formatMessage } = useIntl();
	const [isMenuOpen, { off: closeMenu, on: openMenu }] = useTopMenus(MENU_ID.HELP);
	const isAnonymous = useIsAnonymous();
	const [topMenusValue, { off: closeAllMenus }] = useTopMenus();
	const [, { setNavigationData }] = useHelpPanelActions();
	const { releaseNotesNotifications, setReleaseNotesNotifications } = useHelpButtonState();

	const helpButtonRef = useRef<HTMLButtonElement>(null);

	// go/jfe-eslint
	// eslint-disable-next-line react-hooks/react-compiler
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const attributes: {
		helpPanelVersion: string;
		helpPanelType: string;
		nextGenProject: boolean;
	} = fg('jfp-magma-stability-check-nav4-batch-2')
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useMemo(
				() => ({
					helpPanelVersion: HELP_PANEL_VERSION,
					helpPanelType: 'inProductHelp',
					nextGenProject: isProjectSimplified,
				}),
				[isProjectSimplified],
			)
		: {
				helpPanelVersion: HELP_PANEL_VERSION,
				helpPanelType: 'inProductHelp',
				nextGenProject: isProjectSimplified,
			};

	const triggerAnalytics = useNavigationItemAnalytics({
		navigationItemId: NAVIGATION_ITEM_ID.HELP,
	});

	const { openRightSidebar, closeCurrentRightSidebar } = useRightSidebarController();
	const currentRightSidebarState = useRightSidebarState();
	const isHelpPanelOpenedPreviously = usePrevious(currentRightSidebarState);
	const testIdPrefix = testIdGenerate('secondary-actions', 'help');

	const onClick = useCallback(() => {
		if (
			currentRightSidebarState &&
			currentRightSidebarState.panelId === HELP_PANEL_ID &&
			!currentRightSidebarState.isCollapsed
		) {
			closeCurrentRightSidebar();
		} else {
			openRightSidebar(HELP_PANEL_ID, HELP_PANEL_WIDTH, false, true);
		}

		closeAllMenus();
		setNavigationData({ articleId: { id: '', type: ARTICLE_TYPE.HELP_ARTICLE }, history: [] });

		// Shows if the help panel is currently open or closed
		const isPanelOpen =
			currentRightSidebarState?.panelId === HELP_PANEL_ID && !currentRightSidebarState?.isCollapsed;

		triggerAnalytics({
			...attributes,
			isOpened: !isPanelOpen,
		});
	}, [
		triggerAnalytics,
		attributes,
		currentRightSidebarState,
		closeAllMenus,
		setNavigationData,
		closeCurrentRightSidebar,
		openRightSidebar,
	]);

	const badge = useCallback(() => {
		if (isAnonymous) {
			return null;
		}

		return <BadgeWithoutClient releaseNotesNotifications={releaseNotesNotifications ?? 0} />;
	}, [isAnonymous, releaseNotesNotifications]);

	// Restore the notifications count when the help panel is opened and the notifications count is not 0
	// We explicitly check for the collapsed state, as currentRightSidebarState is null when the sidebar is closed
	// and we don't want to set the notifications count to 0 in that case
	const shouldRestoreNotificationsCount =
		currentRightSidebarState?.isCollapsed === false && releaseNotesNotifications !== 0;

	useEffect(() => {
		if (shouldRestoreNotificationsCount) {
			setReleaseNotesNotifications(0);
		}
	}, [setReleaseNotesNotifications, shouldRestoreNotificationsCount]);

	useEffect(() => {
		if (
			currentRightSidebarState?.panelId === HELP_PANEL_ID &&
			!currentRightSidebarState?.isCollapsed
		) {
			openMenu();
		} else {
			closeMenu();
			if (isHelpPanelOpenedPreviously?.panelId === HELP_PANEL_ID && !currentRightSidebarState) {
				requestAnimationFrame(() => helpButtonRef.current?.focus());
			}
		}
	}, [closeMenu, currentRightSidebarState, isHelpPanelOpenedPreviously, openMenu, topMenusValue]);

	return (
		<div
			data-testid={testIdConcat(testIdPrefix, 'menu-trigger')}
			data-vc={`atlassian-navigation-help${__SERVER__ ? '-ssr' : ''}`}
		>
			<SpotlightTargetComponent>
				<Trigger
					aria-expanded={false}
					aria-haspopup={false}
					isSelected={isMenuOpen}
					badge={badge}
					onClick={onClick}
					tooltip={formatMessage(messages.tooltip)}
					label={formatMessage(messages.label)}
					ref={helpButtonRef}
				/>
			</SpotlightTargetComponent>
		</div>
	);
}

export default HelpNew;
